/* eslint-disable react/jsx-key */
/* eslint-disable react/no-children-prop */
import React, { useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    BasicCard,
    BasicForm,
    BasicTab,
    DangerAlert,
    Empty,
    HorizontalMenu,
    Icon,
    IconName,
    IconTextButton,
    IMenuItem,
    IStatisticProps,
    notification,
    RowLayout,
    Skeleton,
    Spinner,
    Statistic,
    Typography,
    useForm,
} from 'ui-lib';
import { Route } from 'core';
import deviceCss from './devices.css';
import { useAuthStore, useStores } from '../../../../../store';
import { deviceActionConfirm } from '../../../admin/utils';
import { observer } from 'mobx-react';
import { DeviceInfosStore } from '../../../../../store/device-details/device-info-store';

import {
    DEVICE_STATUS,
    DeviceActionName,
    IConnectionSummary,
    IDeviceInventoryStateOptions,
    SCR_TAMPER_STATUS
} from '../../../../../dto/device-dto';
import { MESSAGE_TYPE, NOTIFICATION_TYPE } from '../../../../../dto/notifications-dto';
import { IVarAccountProps, VarModalComponent } from '../../../components/var-select-modal';
import { getErrorMessage, isNullOrEmpty, sleep, TIMEZONES } from 'common-utils';
import { KEY_ROUTE_CODES } from '../../../keys';
import { hasPermission } from '../../../utils';
import { DEVICES_ROUTE_CODES } from '../..';
import { DeviceUtils, isOnlineDevice } from '../../../../../common-utils/device-utils';
import { HorizontalCollapseLayout } from 'ui-lib/components/layout/horizontal-collapse-layout';
import { COMMISSION_STATUS } from '../../commissioning-upload';
import { isCommissionable } from '../utils';
import { InteractiveStatisticTextFieldItem } from './device-attribute-components/interactive-statistic-text-field-item';
import { InteractiveSubcardTextFieldItem } from './device-attribute-components/interactive-subcard-text-field-item';
import { InteractiveSubcardSelectFieldItem } from './device-attribute-components/interactive-subcard-select-field-item';
import { useAlertMessages } from 'pages/private/components/alert/use-messages';
import { MessagesAlert } from 'pages/private/components/alert/messages-alert';
import { usePromiseMemo } from 'utils/common-utils';
import { DeviceSubscriptionTopic, useSocketSubscription } from 'hooks/use-socket-subscription.hook';
import { IDeviceInfoProps } from 'store/devices-store';
import { useDevicePermissionContext } from 'contexts/permissions/device-permission.context';
import { DEVICE_COMPONENTS, StatisticItem, SubCardComponent } from './components';

interface IExecuteCommandProps {
    payload: { commandCode: string; targetDeviceNames: string[] };
    executeTitle: string;
    finishTitle: (status: string) => string;
}

interface IDeviceDetailsProps extends IVarAccountProps {
}
type SectionRefs = {
    jobs?: any;
};
const DeviceDetails = observer((props: IDeviceDetailsProps) => {
    const { varOrClientAccountId: accountId } = props;

    const state: IDeviceInventoryStateOptions = useLocation().state || {};
    const uid = useParams()?.uid;
    const { currentUser } = useAuthStore();
    const navigate = useNavigate();
    const { notificationsStore, deviceStore: devicesStore, deviceGroupsStore, packagesStore, routeStore, accountConfigurationStore } = useStores();

    const stores = useStores();
    const deviceInventoryRoute = routeStore.getRoute(DEVICES_ROUTE_CODES.INVENTORY);
    const decommissioningRoute = routeStore.getRoute(DEVICES_ROUTE_CODES.DECOMMISSIONING);
    const fileTransferRoute = routeStore.getRoute(DEVICES_ROUTE_CODES.FILE_TRANSFER);
    const shellCommandRoute = routeStore.getRoute(DEVICES_ROUTE_CODES.SHELL_COMMAND);
    const keyDistributionRoute = routeStore.getRoute(KEY_ROUTE_CODES.DISTRIBUTION);

    const deviceInfosStore = React.useMemo(
        () => new DeviceInfosStore({ accountId, uid, devicesStore }),
        [accountId],
    );
    const [loading, setLoading] = React.useState<boolean>(true);
    const validDevice = React.useMemo(() => !isNullOrEmpty(deviceInfosStore.info), [deviceInfosStore.info]);
    const [timezonesDisplay, setTimezonesDisplay] = React.useState([]);
    const { errorMessages, addErrorMessages, clearErrorMessages } = useAlertMessages();
    const [form] = useForm();
    const { hasActionPermission } = useDevicePermissionContext();

    const registerRef = useCallback(
        (key: string) => (instance: unknown) => {
            componentRefs.current[key] = instance;
        },
        [],
    );
    const componentRefs = React.useRef<SectionRefs>({});

    const deviceGroup = React.useMemo(() => {
        return deviceGroupsStore.getDeviceGroupByName(DeviceUtils.getDeviceGroupName(deviceInfosStore.info));
    }, [deviceInfosStore.info, deviceGroupsStore.allDeviceGroups]);

    const platform = React.useMemo(() => deviceGroup?.deviceGroupPlatform, [deviceGroup]);

    React.useEffect(() => {
        if (!accountId) return;

        (async () => {
            clearErrorMessages();
            setLoading(true);
            try {
                await packagesStore.loadEntities(accountId);
                const device = await devicesStore.getDevicesByName(accountId, uid);
                if (!device) return;
                // Calling async to do not block the UI
                deviceInfosStore.initializeData(device);
                setTimezonesDisplay(TIMEZONES);
            } catch (err) {
                addErrorMessages(getErrorMessage(err));
            } finally {
                setLoading(false);
            }
        })();
    }, [accountId]);

        const externalSettingsVisibilityStatus = usePromiseMemo(undefined, async () => {
            try {
                return await accountConfigurationStore.getExternalVisibility(currentUser.accountId);
            } catch (err) {
                return false;
            }
        }, []);
    
    const connectivitySubscription = useSocketSubscription<{ uid: string; accountId: number }, { connectivity: IConnectionSummary }>({
        topic: DeviceSubscriptionTopic.CONNECTIVITY,
        buildSubscribePayload: () => ({ accountId, uid }),
        buildUnsubscribePayload: () => ({ accountId, uid }),
        onEvent: (response) => {
            if (deviceInfosStore.loading || !validDevice) return;
            deviceInfosStore.updateConnectivityStatus(response.connectivity);
        }
    });

    const deviceInfoSubscription = useSocketSubscription<{ uid: string; accountId: number }, IDeviceInfoProps>({
        topic: DeviceSubscriptionTopic.DEVICE_INFO,
        buildSubscribePayload: () => ({ accountId, uid }),
        buildUnsubscribePayload: () => ({ accountId, uid }),
        onEvent: (deviceInfo) => {
            if (deviceInfosStore.loading || !validDevice) return;
            deviceInfosStore.updateDeviceInfo(deviceInfo);
        },
    });

    // ------------------------------------------------ UTILS ------------------------------------------------------//

    const executeCommand = async (options: IExecuteCommandProps): Promise<void> => {
        notification['info']({
            message: options.executeTitle,
            description: 'Please wait...',
            placement: 'topRight',
        });

        await notificationsStore.create({
            content: {
                title: 'Remote command',
                text: options.executeTitle,
                messageType: MESSAGE_TYPE.REMOTE_COMMAND,
            },
            type: NOTIFICATION_TYPE.PERSONAL,
            receiverUserUuid: currentUser.uuid,
            sender: currentUser.userName,
        });

        const result = await devicesStore.triggerRemoteCommand(accountId, options.payload);
        await sleep(10000); // not ideal, temporary fix until we refactor I will redesign this
        const data = await devicesStore.getRemoteCommandStatusByExecutionId(accountId, result.executionId);
        componentRefs.current['Jobs']?.refresh?.();
        notification['info']({
            message: options.finishTitle(data.status) + ` for device serial ${deviceInfosStore.serialNumber}`,
            description: 'Please check device jobs for more information...',
            placement: 'topRight',
        });

        await notificationsStore.create({
            content: {
                title: 'Remote command',
                text: options.finishTitle(data.status) + ` for device serial ${deviceInfosStore.serialNumber}`,
                messageType: MESSAGE_TYPE.REMOTE_COMMAND,
            },
            type: NOTIFICATION_TYPE.PERSONAL,
            receiverUserUuid: currentUser.uuid,
            sender: currentUser.userName,
        });
    };

    const reboot = async () => {
        const commandPayload = {
            commandCode: 'reboot',
            targetDeviceNames: [uid],
        };
        await executeCommand({
            executeTitle: `Restart is executing for device serial ${deviceInfosStore?.serialNumber}`,
            finishTitle: (status: string) => `Restart status: ${status}`,
            payload: commandPayload,
        });
        await notificationsStore.create({
            content: {
                title: 'Restart device',
                text: `Restart for serial number: ${deviceInfosStore?.serialNumber} and eSN: ${deviceInfosStore?.esn} has completed`,
                messageType: MESSAGE_TYPE.DEVICE,
            },
            type: NOTIFICATION_TYPE.PERSONAL,
            receiverUserUuid: currentUser.uuid,
            sender: currentUser.userName,
        });
    };

    const restartDevice = () => {
        deviceActionConfirm('restart', reboot, accountId);
    };

    const ping = async () => {
        const commandPayload = {
            commandCode: 'ping',
            targetDeviceNames: [uid],
        };
        await executeCommand({
            executeTitle: `Ping is executing for device serial ${deviceInfosStore?.serialNumber}`,
            finishTitle: (status: string) => `Ping status: ${status}`,
            payload: commandPayload,
        });
    };

    const pingDevice = () => {
        deviceActionConfirm('ping', ping, accountId);
    };

    // ------------------------------------------------ PAGE ELEMENTS ------------------------------------------------------//

    const DeviceBaseDetails = observer(() => {

        const _createSubMenuItemWithRoutePermission = (subMenuItems: IMenuItem[], route: Route, onClick: () => void, label?: string): void => {
            if (!hasPermission(route.code, stores)) return;

            subMenuItems.push({
                code: route.code,
                label: () => label || route.label,
                iconName: route.iconName,
                onClick,
            });
        };

        const _createSubMenuItemWithPermission = (subMenuItems: IMenuItem[], itemOptions: { code: DeviceActionName; onClick: () => void; label: string; iconName: IconName }): void => {
            if (!hasActionPermission(itemOptions.code)) return;

            subMenuItems.push({
                code: itemOptions.code,
                label: () => itemOptions.label,
                iconName: itemOptions.iconName,
                onClick: itemOptions.onClick,
            });
        };

        const _onAction = (path: string, setSelectedOrganisation = true) => {
            if (setSelectedOrganisation) {
                stores.commonStore.setSelectedOrganisation(accountId);
            }
            navigate(path, { state: { uid: uid } });
        };
    
        const subMenuItems: IMenuItem[] = [];
        _createSubMenuItemWithPermission(subMenuItems, {
            code: DeviceActionName.CHECK_HEALTH,
            onClick: pingDevice,
            label: 'Check health',
            iconName: 'circle-check',
        });
        _createSubMenuItemWithPermission(subMenuItems, {
            code: DeviceActionName.RESTART,
            onClick: restartDevice,
            label: 'Restart',
            iconName: 'arrows-rotate',
        });
        _createSubMenuItemWithRoutePermission(subMenuItems, shellCommandRoute, () => _onAction(shellCommandRoute.path));
        _createSubMenuItemWithRoutePermission(subMenuItems, fileTransferRoute, () => _onAction(fileTransferRoute.path));
        _createSubMenuItemWithRoutePermission(subMenuItems, keyDistributionRoute, () => _onAction(keyDistributionRoute.path), 'Resend device keys');
        _createSubMenuItemWithRoutePermission(subMenuItems, decommissioningRoute, () => _onAction(decommissioningRoute.path));

        _createSubMenuItemWithPermission(subMenuItems, {
            code: DeviceActionName.RESEND_GPG_KEYS,
            label: 'Resend GPG keys',
            iconName: 'key' as IconName,
            onClick: async () => {
                try {
                    notification['info']({
                        message: 'Distribute GPG keys job actioned',
                        description: 'Please wait...',
                        placement: 'topRight',
                    });
                    const excutionId = await devicesStore.distributeGPGKeys(accountId, uid);
                    notification['info']({
                        message: 'Distribute GPG keys actions have been issued',
                        description: `Please refresh and check the Device Jobs table with id: ${excutionId}`,
                        placement: 'topRight',
                    });
                    await notificationsStore.create({
                        content: {
                            title: 'GPG keys',
                            text: 'Distribute GPG keys actions have been issued',
                            messageType: MESSAGE_TYPE.GPG_KEYS,
                        },
                        type: NOTIFICATION_TYPE.PERSONAL,
                        receiverUserUuid: currentUser.uuid,
                        sender: currentUser.userName,
                    });
                } catch (error) {
                    notification['error']({
                        message: 'Distribute GPG keys - error occurred.',
                        description: `${error.message}`,
                        placement: 'topRight',
                    });
                    await notificationsStore.create({
                        content: {
                            title: 'GPG keys',
                            text: 'Distribute GPG keys - error occurred.',
                            messageType: MESSAGE_TYPE.GPG_KEYS,
                        },
                        type: NOTIFICATION_TYPE.PERSONAL,
                        receiverUserUuid: currentUser.uuid,
                        sender: currentUser.userName,
                    });
                }
            },
        });

        if (externalSettingsVisibilityStatus) {
            _createSubMenuItemWithPermission(subMenuItems, {
                code: DeviceActionName.RESEND_EXTERNAL_REPO_CONFIG,
                label: 'Resend External Repo config',
                iconName: 'key' as IconName,
                onClick: async () => {
                    try {
                        notification['info']({
                            message: 'Distribute External Repo configuration job actioned',
                            description: 'Please wait...',
                            placement: 'topRight',
                        });

                        const result = await devicesStore.distributeExternalRepoConfiguration(accountId,uid);
                        notification['info']({
                            message: 'Distribute External Configuration actions have been issued',
                            description: `${result.message}`,
                            placement: 'topRight',
                        });
                        await notificationsStore.create({
                            content: {
                                title: 'External Repo config',
                                text: 'Distribute External Configuration actions have been issued',
                                messageType: MESSAGE_TYPE.EXTERNAL_REPO_CONFIG_DISTRIBUTION,
                            },
                            type: NOTIFICATION_TYPE.PERSONAL,
                            receiverUserUuid: currentUser.uuid,
                            sender: currentUser.userName,
                        });
                    } catch (error) {
                        notification['error']({
                            message: 'Distribute External Config - error occurred.',
                            description: `${error.message}`,
                            placement: 'topRight',
                        });
                        await notificationsStore.create({
                            content: {
                                title: 'External Repo config',
                                text: 'Distribute External Config - error occurred.',
                                messageType: MESSAGE_TYPE.EXTERNAL_REPO_CONFIG_DISTRIBUTION,
                            },
                            type: NOTIFICATION_TYPE.PERSONAL,
                            receiverUserUuid: currentUser.uuid,
                            sender: currentUser.userName,
                        });
                    }
                },
            });
        }

        const ActionsMenu = () => {
            return (
                <HorizontalMenu
                    className={deviceCss['actions-menu']}
                    menuItems={[
                        {
                            code: 'actionsMenu',
                            iconName: 'gear',
                            iconSize: 'SMALL',
                            subMenuItems,
                        },
                    ]}
                />
            );
        };

        const items = [
            <InteractiveStatisticTextFieldItem
                attributeName='serialNumber'
                deviceInfosStore={deviceInfosStore}
                initialValue={deviceInfosStore.serialNumber}
                form={form}
                title='Serial Number'
            />,
            <InteractiveStatisticTextFieldItem
                attributeName='esn'
                deviceInfosStore={deviceInfosStore}
                initialValue={deviceInfosStore.esn}
                form={form}
                title='eSN'
                />,
            <InteractiveStatisticTextFieldItem
                attributeName='name'
                initialValue={deviceInfosStore.name}
                deviceInfosStore={deviceInfosStore}
                form={form}
                title='Name'
            />,
            <StatisticItem
                title='Firmware Version'
                value={deviceInfosStore.secureVersion}
                suffix={<div className={deviceCss['suffixDetails']}>{`${deviceInfosStore.managedState}`}</div>}
            />,
            <StatisticItem
                valueStyle={{ color: isOnlineDevice(deviceInfosStore.status) ? '#52AB98' : '#d61346', fontWeight: 700 }}
                title='Status'
                value={
                    isCommissionable(deviceInfosStore.status as DEVICE_STATUS, deviceInfosStore.tamperInfo?.status)
                        ? COMMISSION_STATUS.COMMISSIONABLE
                        : deviceInfosStore.status
                }
                valueRender={(node): React.ReactNode =>
                    isNullOrEmpty(deviceInfosStore.status) ? (
                        <Spinner className={deviceCss['status-spinner']} size='medium' />
                    ) : (
                        node
                    )
                }
            />,
            <StatisticItem
                title='Last Online'
                value={
                    isCommissionable(deviceInfosStore.status as DEVICE_STATUS, deviceInfosStore.tamperInfo?.status)
                        ? ''
                        : deviceInfosStore.lastOnline
                }
                valueRender={(node): React.ReactNode =>
                    isNullOrEmpty(deviceInfosStore.status) ? (
                        <Spinner className={deviceCss['status-spinner']} size='medium' />
                    ) : (
                        node
                    )
                }
            />,
            <StatisticItem title='Last Updated' value={deviceInfosStore.lastUpdated} />,
            <StatisticItem title='Actions' suffix={<ActionsMenu />} />,
        ];

        return (
            <BasicForm
                form={form}
                items={[
                    <div className={deviceCss.baseDetails}>
                        {deviceInfosStore.error != undefined && (
                            <DangerAlert closable={false} message="Errors" description={deviceInfosStore.error}/>
                        )}
                        <div className={deviceCss['base-details-rows']}>
                            <RowLayout childItems={items} childCols={[4, 3, 4, 2, 2, 4, 4, 1]} />
                        </div>
                    </div>,
                ]}
            />
        );
    });

    const BaseDetailTitle = () => {
        return (
            <div className={deviceCss['details-title']}>
                <div className={deviceCss['left']}>Device Details</div>
                <div className={deviceCss['middle']}>Platform: <Typography.Text style={{color: '#52AB98'}}>{platform}</Typography.Text></div>
                <div className={deviceCss['right']}>Device UID: {uid}</div>
            </div>
        );
    };

    const displayBatteryTemperature = () => {
        const deviceDetails: (string | JSX.Element)[] = [
            <span key="scr-battery-span">
                SCR Battery (mV):{' '}
                {deviceInfosStore.info?.deviceInfo?.scrInfo?.hardware?.battery_mV || 'information not found'}
            </span>,
            <span key="scr-temp-span">
                SCR Temp (&#x2103;):{' '}
                {deviceInfosStore.info?.deviceInfo?.scrInfo?.hardware?.temperature_C || 'information not found'}
            </span>,
            <span key="main-board-battery-span">
                Main Board Battery (mV):{' '}
                {deviceInfosStore.taipanBatteryInfo?.batteryInfo?.voltage || 'information not found'}
            </span>,
        ];
        const tamperDetails = deviceInfosStore.tamperInfo?.tamperedList;
        if (!isNullOrEmpty(tamperDetails) && isOnlineDevice(deviceInfosStore.status)) {
            const tamper = tamperDetails.join(', ');
            deviceDetails.push(
                <div>
                    <span className={deviceCss.tamperDetails}>Hardware Tamper Present: </span>
                    {tamper}
                </div>,
            );
        }
        return deviceDetails;
    };

    const leftPanelDetails = (
        <BasicForm
            className={deviceCss['left-panel-details-form']}
            form={form}
            items={[
                <SubCardComponent
                    code='group' 
                    title='Device Group' 
                    contents={[deviceInfosStore.info?.status == DEVICE_STATUS.ASSIGNED ? deviceInfosStore.groupLabel : ""]} 
                />,
                <SubCardComponent
                    code='deviceType'
                    title='Device Type'
                    contents={[deviceInfosStore.info?.deviceTypeName || 'information not found']}
                />,
                <SubCardComponent
                    code='keyInventory'
                    title='Key Inventory'
                    contents={deviceInfosStore.displayedKeys}
                    loading={deviceInfosStore.loading}
                    numOfRows={2}
                />,
                <SubCardComponent
                    code='batteryTemperature'
                    title='Battery and Temperature'
                    loading={deviceInfosStore.loading}
                    contents={displayBatteryTemperature()}
                    numOfRows={4}
                />,
                <InteractiveSubcardSelectFieldItem
                    title='Timezone'
                    attributeName='timezone'
                    deviceInfosStore={deviceInfosStore}
                    dataSource={[{ value: '', label: '' }, ...timezonesDisplay]}
                    initialValue={deviceInfosStore.timezone || ''}
                    form={form}
                    afterFinish={(updated: boolean, value?: string): void => {
                        if (!updated) return;
                        deviceInfosStore.setTimezone(value);
                        if (!isOnlineDevice(deviceInfosStore.status)) {
                            notification['info']({
                                message: 'Time zone will be updated when device is online.',
                                placement: 'topRight',
                            });
                        }
                    }}
                />,
                <InteractiveSubcardTextFieldItem
                    attributeName='location'
                    deviceInfosStore={deviceInfosStore}
                    initialValue={deviceInfosStore.location}
                    title='Location'
                    form={form}
                />,
                <SubCardComponent code='emvVersion' title='EMV Version' contents={[deviceInfosStore.emvVersion]} />,
                <SubCardComponent
                    code='mspVersion'
                    title='MSP Version'
                    contents={[deviceInfosStore.mspVersion || 'information not found']}
                    loading={deviceInfosStore.loading}
                    numOfRows={1}
                />,
                <SubCardComponent
                    code='scrStatus'
                    title='SCR Status'
                    contents={[
                        <span>
                            <strong
                                style={{
                                    textTransform: 'capitalize',
                                    color:
                                        deviceInfosStore.tamperInfo?.status == SCR_TAMPER_STATUS.SECURE
                                            ? '#52AB98'
                                            : '#d61346',
                                }}
                            >
                                {deviceInfosStore.tamperInfo?.status || 'information not found'}
                            </strong>
                        </span>,
                    ]}
                />,
            ]}
        />
    );

    return (
        <div className={deviceCss['device-details']}>
            <Skeleton
                loading={loading}
                children={
                    <div>
                        {!validDevice ? (
                            <Empty
                                className={deviceCss.emptyComponent}
                                description={
                                    <h3>{`Can't find the device with uid: ${uid} serial: ${
                                        deviceInfosStore.serialNumber || ''
                                    } esn: ${deviceInfosStore.esn || ''} `}</h3>
                                }
                            />
                        ) : (
                            <React.Fragment key='device-details-fragment'>
                                <MessagesAlert className={deviceCss.emptyComponent} errorMessages={errorMessages} />
                                <BasicCard
                                    className={deviceCss.device}
                                    title={<BaseDetailTitle />}
                                    bordered={false}
                                    contents={<DeviceBaseDetails />}
                                    size={'default'}
                                />
                                <HorizontalCollapseLayout
                                    defaultExpand={true}
                                    expandIconPosition='end'
                                    expandIcon={(panelProps) => (
                                        <div style={{ marginTop: '5px' }}>
                                            <Icon
                                                iconPrefix='fas'
                                                iconName={panelProps.isActive ? 'caret-left' : 'caret-right'}
                                            />
                                        </div>
                                    )}
                                    panelClassName={deviceCss['horizontal-collapse-panel']}
                                    leftPanelClassName={deviceCss['left-panel-details']}
                                    leftPanel={leftPanelDetails}
                                    rightPanelClassName={deviceCss['right-panel-details']}
                                    rightPanel={
                                        <BasicTab
                                            defaultActiveIndex={0}
                                            tabPanes={DEVICE_COMPONENTS[platform ?? 'ASSURE']?.map(({ label, render }) => ({
                                                label: <Typography.Text strong>{label}</Typography.Text>,
                                                pane: render({
                                                    accountId,
                                                    device: {
                                                        deviceName: deviceInfosStore.info.deviceName,
                                                        serialNumber: deviceInfosStore.serialNumber,
                                                        trampolineVersion: deviceInfosStore.trampolineVersion,
                                                        isOnline: isOnlineDevice(deviceInfosStore.status),
                                                        platform,
                                                    },
                                                    ref: registerRef(label),
                                                }),
                                            }))}
                                        />
                                    }
                                />
                            </React.Fragment>
                        )}
                        <IconTextButton
                            type='primary'
                            className={deviceCss.backBtn}
                            label='Back to Device Inventory'
                            onClick={() =>
                                navigate(deviceInventoryRoute.path, {
                                    state: { ...state, accountId: accountId },
                                })
                            }
                        />
                    </div>
                }
            />
        </div>
    );
});

export const VarModalDeviceDetails = VarModalComponent(DeviceDetails);
